import classNames from 'classnames';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React, { cloneElement } from 'react';

import CloseIcon from '../../../static/icons/close.svg';
import BitmapSVG from '../../components/BitmapSVG';
import Img from '../../components/ImageLoader/Image';
import ButtonT3 from '../../components/lib/ButtonT3/ButtonT3';
import { messages as AccessibilityMessages } from '../../constants/AccessibilityConstants';
import { ModalStyles } from '../../constants/Modal';
import QA from '../../constants/QATags';
import { jeCSS } from '../../lib/StyleUtils';
import { createBEM } from '../../lib/utils/css';
import Icon from '../Icon/Icon';

import './Modal.scss';

/**
 * Base container for modals
 *
 * @namespace {object} props
 * @property {string} props.className - className to add to the root element
 * @property {node} props.children - child components
 */
export class ModalContainer extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    isBlueOverlay: PropTypes.bool,
    isWideModal: PropTypes.bool,
    isSmall: PropTypes.bool,
  };

  render() {
    // @TODO WEBCLIENT-3995: Clean up classnames, implement BEM for Modal.jsx
    const classes = classNames([
      'Modal',
      { 'Modal--small': this.props.isSmall },
      {
        [this.props.className]: this.props.className,
      },
    ]);

    const containerClasses = classNames([
      'Modal__container',
      { 'Modal__container--wide': this.props.isWideModal },
    ]);

    let overlay = null;
    const classList = this.props.className && this.props.className.split(' ');
    if (
      !classList ||
      (classList.indexOf('Modal--coachmark') === -1 && classList.indexOf('Modal--upsell') === -1)
    ) {
      const overlayClasses = createBEM('Modal__overlay', {
        blue: this.props.isBlueOverlay,
      });

      overlay = <div className={overlayClasses}></div>;
    }
    return (
      <div className={classes}>
        {overlay}
        <div className={containerClasses}>{this.props.children}</div>
      </div>
    );
  }
}

/**
 * The top row with an 'x' button
 *
 * @namespace {object} props
 * @property {Function} props.onClose - the function to call when the 'x' button is clicked
 */
export class ModalXRow extends React.Component {
  static propTypes = {
    onClose: PropTypes.func,
  };

  render() {
    return (
      <button
        type='button'
        className='Modal__container__x'
        onClick={this.props.onClose}
        data-qa={QA.CPXB}
        title={AccessibilityMessages.closeModal.defaultMessage}
      >
        <Icon className='icon-close' glyph={CloseIcon} />
      </button>
    );
  }
}

/**
 * A standard title bar for modals
 *
 * @namespace {object} props
 * @property {node} props.children - child components
 */
export class ModalTitle extends React.Component {
  static propTypes = {
    qaTag: PropTypes.string,
    children: PropTypes.node.isRequired,
    isSubscriptionModal: PropTypes.bool,
  };

  static defaultProps = {
    qaTag: QA.PT,
  };

  render() {
    const classes = classNames('Modal__container__title', {
      'Modal__container__title--subscription': this.props.isSubscriptionModal,
    });

    return (
      <div className={classes} data-qa={this.props.qaTag}>
        {this.props.children}
      </div>
    );
  }
}

/**
 * A standard subtitle bar for modals
 *
 * @namespace {object} props
 * @property {node} props.children - child components
 */
export class ModalSubTitle extends React.Component {
  static propTypes = {
    qaTag: PropTypes.string,
    children: PropTypes.node.isRequired,
  };

  static defaultProps = {
    qaTag: QA.PST,
  };

  render() {
    return (
      <div className='Modal__container__subTitle' data-qa={this.props.qaTag}>
        {this.props.children}
      </div>
    );
  }
}

/**
 * A standard body for modals
 *
 * @namespace {object} props
 * @property {node} props.children - child components
 */
export class ModalBody extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    isSubscriptionModal: PropTypes.bool,
    isTranslucentBodyText: PropTypes.bool,
    isNoMargin: PropTypes.bool,
    isNoMarginWithButtons: PropTypes.bool,
    isLeftAligned: PropTypes.bool,
  };

  render() {
    const classes = classNames('Modal__container__body', {
      'Modal__container__body--subscription': this.props.isSubscriptionModal,
      'Modal__container__body--translucent': this.props.isTranslucentBodyText,
      'Modal__container__body--noMargin': this.props.isNoMargin,
      'Modal__container__body--noMarginWithButtons': this.props.isNoMarginWithButtons,
      'Modal__container__body--leftAligned': this.props.isLeftAligned,
    });

    return (
      <div className={classes} data-qa={QA.PM}>
        {this.props.children}
      </div>
    );
  }
}

/**
 * A standard button row for modals
 *
 * @namespace {object} props
 * @property {Array.<node>} props.buttons - The button components
 */
export class ModalButtonRow extends React.Component {
  static propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.node).isRequired,
    shouldStackAndReverseButtons: PropTypes.bool,
  };

  render() {
    const buttons = this.props.buttons.map((button, id) => cloneElement(button, { key: id }));
    const classes = createBEM('Modal__container__buttons', {
      stackAndReverse: this.props.shouldStackAndReverseButtons,
    });

    if (buttons.length > 0) {
      return <div className={classes}>{buttons}</div>;
    }
  }
}

// ModalIcon is used for Smart Conversion coachmarks.
export class ModalIcon extends React.Component {
  static propTypes = {
    glyph: PropTypes.node,
    svgBitmap: PropTypes.string,
    imageUrl: PropTypes.string,
    length: PropTypes.number,
    styleNames: PropTypes.oneOf(values(ModalStyles)),
  };

  render() {
    let icon;
    if (this.props.glyph) {
      icon = (
        <Icon
          className={jeCSS('Modal__container__icon__icon', [this.props.styleNames])}
          glyph={this.props.glyph}
        />
      );
    } else if (this.props.svgBitmap) {
      icon = (
        <BitmapSVG
          bitmap={this.props.svgBitmap}
          width={this.props.length}
          height={this.props.length}
        />
      );
    } else if (this.props.imageUrl) {
      icon = <Img src={this.props.imageUrl} classes={'Modal__container__icon__img'} />;
    }
    return <div className='Modal__container__icon'>{icon}</div>;
  }
}

export class ModalSingleButton extends React.Component {
  static propTypes = {
    text: PropTypes.node.isRequired,
    action: PropTypes.func.isRequired,
    theme: PropTypes.string,
    isBlock: PropTypes.bool,
    isInline: PropTypes.bool,
    qaTag: PropTypes.string,
  };

  render() {
    const isRightAligned = !(this.props.isBlock || this.props.isInline);

    return (
      <ButtonT3
        theme={this.props.theme}
        isBlock={this.props.isBlock}
        isInline={this.props.isInline}
        isRightAligned={isRightAligned}
        onClick={this.props.action}
        qaTag={this.props.qaTag}
      >
        {this.props.text}
      </ButtonT3>
    );
  }
}
